import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useLocale } from "@/hooks/use-locale";
import { trackNavClick, trackCTAClick, trackLanguageSwitch } from "@/lib/analytics";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

const langOptions = [
  { code: "en", label: "English", prefix: "" },
  { code: "zh", label: "繁體中文", prefix: "/zh" },
  { code: "ja", label: "日本語", prefix: "/ja" },
] as const;

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileLandOpen, setMobileLandOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLang, prefix, pathWithoutLang } = useLocale();

  const buildLangPath = (linkPrefix: string) => {
    const tail = pathWithoutLang === "/" ? "" : pathWithoutLang;
    return `${linkPrefix}${tail}` || "/";
  };

  const handleLangSwitch = (code: string, linkPrefix: string) => {
    trackLanguageSwitch(code);
    navigate(buildLangPath(linkPrefix));
  };

  const shopHref = `${prefix}/queebi-products`;
  const landItems = [
    { label: t("nav.characters"), href: `${prefix}/queebi-land-characters` },
    { label: t("nav.story"), href: `${prefix}/story-of-queebi-land` },
    { label: t("nav.activities"), href: `${prefix}/activities` },
    { label: t("nav.audiobook"), href: `${prefix}/audiobook` },
  ];
  const tailItems = [
    { label: t("nav.blog"), href: `${prefix}/blog` },
    { label: t("nav.aboutUs"), href: `${prefix}/about-us` },
  ];

  return (
    <nav className="sticky top-0 z-50 border-b border-border/40 bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
      <div className="container flex h-16 items-center justify-between">
        <div className="flex items-center gap-1">
          <Link to={prefix || "/"} className="flex items-center" onClick={() => trackNavClick('logo')}>
            <img
              src="/images/brand/logo.png"
              alt="Queebi"
              className="h-9 w-auto"
            />
          </Link>
          {/* Hidden admin entry — intentionally low-visibility */}
          <Link
            to="/admin/login"
            aria-label="Internal"
            title="Internal"
            className="ml-1 h-1.5 w-1.5 rounded-full bg-foreground/10 hover:bg-foreground/60 transition-colors"
          />
        </div>

        <div className="hidden md:flex items-center gap-1">
          <Link
            to={shopHref}
            onClick={() => trackNavClick('shop')}
            className={cn(
              "px-3 py-2 rounded-md text-sm font-semibold transition-colors",
              location.pathname === shopHref
                ? "text-foreground underline underline-offset-4"
                : "text-foreground/70 hover:text-foreground"
            )}
          >
            {t("nav.shop")}
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  "px-3 py-2 rounded-md text-sm font-semibold transition-colors inline-flex items-center gap-1",
                  landItems.some((i) => location.pathname === i.href)
                    ? "text-foreground underline underline-offset-4"
                    : "text-foreground/70 hover:text-foreground"
                )}
              >
                {t("nav.queebiLand")}
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {landItems.map((item) => (
                <DropdownMenuItem
                  key={item.href}
                  onSelect={() => { trackNavClick(item.label); navigate(item.href); }}
                  className="cursor-pointer"
                >
                  {item.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {tailItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => trackNavClick(item.label)}
              className={cn(
                "px-3 py-2 rounded-md text-sm font-semibold transition-colors",
                location.pathname === item.href
                  ? "text-foreground underline underline-offset-4"
                  : "text-foreground/70 hover:text-foreground"
              )}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <div className="hidden md:flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Change language" className="text-foreground/70 hover:text-foreground">
                <Globe className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {langOptions.map((opt) => (
                <DropdownMenuItem
                  key={opt.code}
                  onSelect={() => handleLangSwitch(opt.code, opt.prefix)}
                  className={cn("cursor-pointer", currentLang === opt.code && "font-bold")}
                >
                  {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold transition-transform duration-200 hover:scale-105">
            <a href="https://www.kalianii.com/pages/pacifier-holders" target="_blank" rel="noopener noreferrer" onClick={() => trackCTAClick('usa_official_store_nav')}>
              <span className="leading-tight">{t("nav.usaOfficialStore")}</span>
              <span className="text-[10px] font-bold leading-tight">{t("nav.usaOfficialStoreSub")}</span>
            </a>
          </Button>
        </div>

        <Button
          variant="ghost"
          size="icon"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          className="md:hidden text-foreground"
          onClick={() => setMobileOpen(!mobileOpen)}
        >
          {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-border bg-card px-4 pb-4 pt-2 space-y-1 animate-fade-in">
          <Link
            to={shopHref}
            onClick={() => { setMobileOpen(false); trackNavClick('shop'); }}
            className={cn(
              "block px-3 py-2 rounded-md text-sm font-semibold transition-colors",
              location.pathname === shopHref ? "text-foreground bg-muted" : "text-foreground/70 hover:text-foreground"
            )}
          >
            {t("nav.shop")}
          </Link>

          <button
            type="button"
            onClick={() => setMobileLandOpen(!mobileLandOpen)}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-semibold text-foreground/80"
          >
            {t("nav.queebiLand")}
            <ChevronDown className={cn("h-4 w-4 transition-transform", mobileLandOpen && "rotate-180")} />
          </button>
          {mobileLandOpen && (
            <div className="pl-4 space-y-1">
              {landItems.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => { setMobileOpen(false); trackNavClick(item.label); }}
                  className={cn(
                    "block px-3 py-2 rounded-md text-sm transition-colors",
                    location.pathname === item.href ? "text-foreground bg-muted" : "text-foreground/70 hover:text-foreground"
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          )}

          {tailItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => { setMobileOpen(false); trackNavClick(item.label); }}
              className={cn(
                "block px-3 py-2 rounded-md text-sm font-semibold transition-colors",
                location.pathname === item.href ? "text-foreground bg-muted" : "text-foreground/70 hover:text-foreground"
              )}
            >
              {item.label}
            </Link>
          ))}

          <div className="flex gap-2 px-3 py-2">
            {langOptions.map((opt) => (
              <button
                key={opt.code}
                type="button"
                onClick={() => { setMobileOpen(false); handleLangSwitch(opt.code, opt.prefix); }}
                className={cn(
                  "px-3 py-1 rounded-full text-xs font-semibold border transition-transform duration-200 hover:scale-105",
                  currentLang === opt.code
                    ? "bg-foreground text-background border-foreground"
                    : "border-border text-foreground/70 hover:text-foreground"
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <a
            href="https://www.kalianii.com/pages/pacifier-holders"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCTAClick('usa_official_store_mobile')}
            className="block px-3 py-2 mt-2 rounded-full bg-primary text-primary-foreground text-center text-sm font-bold"
          >
            <span className="block leading-tight">{t("nav.usaOfficialStore")}</span>
            <span className="block text-[10px] font-bold leading-tight">{t("nav.usaOfficialStoreSub")}</span>
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
